import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn, formatNumber, formatPercent, timeAgo, formatDate, formatDuration, getStatusColor, formatBytes } from '@/lib/utils';
import { apiService as mockApi } from '@/api';
import { Card, CardHeader, CardTitle, CardBody, StatCard } from '@/components/ui/Card';
import { Badge, StatusDot } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Modal, ConfirmDialog } from '@/components/ui/Modal';
import { Tabs } from '@/components/ui/Tabs';
import { Input, Select, Label, Checkbox } from '@/components/ui/Input';
import { LoadingState, EmptyState } from '@/components/ui/States';
import { toast } from '@/components/ui/Toast';
import { PageHeader, PageContainer } from '@/components/layout/PageHeader';
import type { Device, PatchPolicy, PatchInfo, Job } from '@/types';
import {
  ShieldAlert, ShieldCheck, RefreshCw, Plus, Search, Filter, AlertTriangle,
  CheckCircle2, XCircle, Clock, Server, Play, Eye, Settings, FileText, ArrowRight,
  RotateCcw, Download, Cpu, Activity, Layers, Check, ChevronRight, Info
} from 'lucide-react';

interface AggregatedMissingPatch {
  kb: string;
  title: string;
  severity: 'optional' | 'important' | 'critical';
  category: string;
  affectedDevices: { deviceId: string; hostname: string; client: string; site: string }[];
  sizeMb: number;
}

interface InstalledPatchRecord {
  id: string;
  kb: string;
  title: string;
  deviceId: string;
  hostname: string;
  client: string;
  site: string;
  category: string;
  installedDate: string;
}

interface FailedPatchRecord {
  id: string;
  kb: string;
  title: string;
  deviceId: string;
  hostname: string;
  client: string;
  site: string;
  category: string;
  date: string;
  error: string;
}

export function Patches() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [devices, setDevices] = useState<Device[]>([]);
  const [policies, setPolicies] = useState<PatchPolicy[]>([]);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);

  // Filters state
  const [missingSearch, setMissingSearch] = useState('');
  const [missingSeverity, setMissingSeverity] = useState<string>('all');
  const [missingCategory, setMissingCategory] = useState<string>('all');

  const [installedSearch, setInstalledSearch] = useState('');
  const [installedCategory, setInstalledCategory] = useState<string>('all');

  const [failedSearch, setFailedSearch] = useState('');

  const [historySearch, setHistorySearch] = useState('');
  const [historyStatus, setHistoryStatus] = useState<string>('all');

  // Policy Modal state
  const [isPolicyModalOpen, setIsPolicyModalOpen] = useState(false);
  const [newPolicyName, setNewPolicyName] = useState('');
  const [newPolicyRing, setNewPolicyRing] = useState<'ring1' | 'ring2' | 'ring3' | 'ring4'>('ring2');
  const [newPolicyApproval, setNewPolicyApproval] = useState<'manual' | 'automatic'>('automatic');
  const [newPolicyDay, setNewPolicyDay] = useState('Sunday');
  const [newPolicyStartHour, setNewPolicyStartHour] = useState(2);
  const [newPolicyDuration, setNewPolicyDuration] = useState(4);
  const [newPolicyReboot, setNewPolicyReboot] = useState<'never' | 'when_required' | 'always'>('when_required');
  const [newPolicyRetryCount, setNewPolicyRetryCount] = useState(3);
  const [newPolicyRetryDelay, setNewPolicyRetryDelay] = useState(4);
  const [newPolicyExclusions, setNewPolicyExclusions] = useState('');

  // Selected missing patches state
  const [selectedMissingKbs, setSelectedMissingKbs] = useState<string[]>([]);
  const [installingKb, setInstallingKb] = useState<string | null>(null);
  const [retryingPatchId, setRetryingKb] = useState<string | null>(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [devRes, polRes, jobRes] = await Promise.all([
        mockApi.getDevices({ pageSize: 500 }),
        mockApi.getPatchPolicies(),
        mockApi.getJobs(),
      ]);
      setDevices(devRes.data || []);
      setPolicies(Array.isArray(polRes) ? polRes : (polRes as any)?.data || []);
      const jobList = Array.isArray(jobRes) ? jobRes : (jobRes as any)?.data || [];
      setJobs(jobList.filter((j: any) => j.type === 'patch') || []);
    } catch (err) {
      toast('Failed to load patch management data', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Summary Metrics
  const metrics = useMemo(() => {
    const total = devices.length;
    if (total === 0) return { compliancePercent: 0, compliant: 0, missing: 0, failed: 0, criticalMissing: 0, importantMissing: 0 };
    const compliant = devices.filter((d) => d.patchStatus === 'compliant').length;
    const missing = devices.filter((d) => d.patchStatus === 'missing_updates').length;
    const failed = devices.filter((d) => d.patchStatus === 'failed_updates').length;
    const compliancePercent = Math.round((compliant / total) * 100);

    let criticalMissing = 0;
    let importantMissing = 0;
    devices.forEach((d) => {
      (d.patches ?? []).forEach((p) => {
        if (p.status === 'missing') {
          if (p.severity === 'critical') criticalMissing++;
          if (p.severity === 'important') importantMissing++;
        }
      });
    });

    return { compliancePercent, compliant, missing, failed, criticalMissing, importantMissing };
  }, [devices]);

  // Aggregated Missing Updates Across All Devices
  const aggregatedMissing = useMemo<AggregatedMissingPatch[]>(() => {
    const map = new Map<string, AggregatedMissingPatch>();
    devices.forEach((device) => {
      (device.patches ?? []).forEach((patch) => {
        if (patch.status === 'missing') {
          const existing = map.get(patch.kb);
          if (existing) {
            existing.affectedDevices.push({
              deviceId: device.id,
              hostname: device.hostname,
              client: device.client,
              site: device.site,
            });
          } else {
            map.set(patch.kb, {
              kb: patch.kb,
              title: patch.title,
              severity: patch.severity,
              category: patch.category || 'Security Updates',
              sizeMb: patch.sizeMb || 120,
              affectedDevices: [
                {
                  deviceId: device.id,
                  hostname: device.hostname,
                  client: device.client,
                  site: device.site,
                },
              ],
            });
          }
        }
      });
    });
    return Array.from(map.values());
  }, [devices]);

  // Installed Patches List
  const installedPatches = useMemo<InstalledPatchRecord[]>(() => {
    const list: InstalledPatchRecord[] = [];
    devices.forEach((device) => {
      (device.patches ?? []).forEach((patch, idx) => {
        if (patch.status === 'installed') {
          list.push({
            id: `${device.id}-${patch.kb}-${idx}`,
            kb: patch.kb,
            title: patch.title,
            deviceId: device.id,
            hostname: device.hostname,
            client: device.client,
            site: device.site,
            category: patch.category || 'Cumulative Updates',
            installedDate: patch.installedDate || new Date(Date.now() - (idx + 1) * 86400000).toISOString(),
          });
        }
      });
    });
    return list.sort((a, b) => new Date(b.installedDate).getTime() - new Date(a.installedDate).getTime());
  }, [devices]);

  // Failed Patches List
  const failedPatches = useMemo<FailedPatchRecord[]>(() => {
    const list: FailedPatchRecord[] = [];
    const errorCodes = [
      '0x80070005 - Access Denied',
      '0x80240020 - Installation timed out',
      '0x800705b4 - Windows Update timeout',
      '0x80240034 - Update manifest missing',
      '0x80070002 - The system cannot find the file specified',
    ];
    devices.forEach((device) => {
      (device.patches ?? []).forEach((patch, idx) => {
        if (patch.status === 'failed') {
          list.push({
            id: `${device.id}-${patch.kb}-${idx}`,
            kb: patch.kb,
            title: patch.title,
            deviceId: device.id,
            hostname: device.hostname,
            client: device.client,
            site: device.site,
            category: patch.category || 'Security Updates',
            date: new Date(Date.now() - (idx + 1) * 43200000).toISOString(),
            error: errorCodes[idx % errorCodes.length],
          });
        }
      });
    });
    return list;
  }, [devices]);

  // Filtered Missing Patches
  const filteredMissing = useMemo(() => {
    return aggregatedMissing.filter((patch) => {
      const q = missingSearch.toLowerCase();
      const matchesSearch = patch.kb.toLowerCase().includes(q) || patch.title.toLowerCase().includes(q);
      const matchesSeverity = missingSeverity === 'all' || patch.severity === missingSeverity;
      const matchesCategory = missingCategory === 'all' || patch.category === missingCategory;
      return matchesSearch && matchesSeverity && matchesCategory;
    });
  }, [aggregatedMissing, missingSearch, missingSeverity, missingCategory]);

  // Categories list
  const missingCategories = useMemo(() => {
    const set = new Set<string>();
    aggregatedMissing.forEach((p) => set.add(p.category));
    return Array.from(set);
  }, [aggregatedMissing]);

  // Filtered Installed Patches
  const filteredInstalled = useMemo(() => {
    return installedPatches.filter((patch) => {
      const q = installedSearch.toLowerCase();
      const matchesSearch =
        patch.kb.toLowerCase().includes(q) ||
        patch.title.toLowerCase().includes(q) ||
        patch.hostname.toLowerCase().includes(q);
      const matchesCategory = installedCategory === 'all' || patch.category === installedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [installedPatches, installedSearch, installedCategory]);

  // Filtered Failed Patches
  const filteredFailed = useMemo(() => {
    return failedPatches.filter((patch) => {
      const q = failedSearch.toLowerCase();
      return (
        patch.kb.toLowerCase().includes(q) ||
        patch.title.toLowerCase().includes(q) ||
        patch.hostname.toLowerCase().includes(q) ||
        patch.error.toLowerCase().includes(q)
      );
    });
  }, [failedPatches, failedSearch]);

  // Filtered Jobs History
  const filteredJobs = useMemo(() => {
    return jobs.filter((job) => {
      const q = historySearch.toLowerCase();
      const matchesSearch = job.name.toLowerCase().includes(q) || job.id.toLowerCase().includes(q);
      const matchesStatus = historyStatus === 'all' || job.status === historyStatus;
      return matchesSearch && matchesStatus;
    });
  }, [jobs, historySearch, historyStatus]);

  // Actions
  const handleTogglePolicy = (policyId: string) => {
    setPolicies((prev) =>
      prev.map((p) => {
        if (p.id === policyId) {
          const next = !p.enabled;
          toast(`Policy "${p.name}" ${next ? 'enabled' : 'disabled'}`, 'info');
          return { ...p, enabled: next };
        }
        return p;
      })
    );
  };

  const handleInstallSinglePatch = (kb: string, title: string) => {
    setInstallingKb(kb);
    setTimeout(() => {
      setInstallingKb(null);
      toast(`Patch deployment initiated for ${kb} (${title})`, 'success');
      // Mark patch installed locally for UI responsiveness
      setDevices((prev) =>
        prev.map((d) => ({
          ...d,
          patches: (d.patches ?? []).map((p) => (p.kb === kb ? { ...p, status: 'installed', installedDate: new Date().toISOString() } : p)),
        }))
      );
    }, 800);
  };

  const handleInstallSelected = () => {
    if (selectedMissingKbs.length === 0) return;
    toast(`Initiated deployment for ${selectedMissingKbs.length} selected patch(es)`, 'success');
    setSelectedMissingKbs([]);
  };

  const handleRetryPatch = (record: FailedPatchRecord) => {
    setRetryingKb(record.id);
    setTimeout(() => {
      setRetryingKb(null);
      toast(`Retry initiated for ${record.kb} on ${record.hostname}`, 'success');
    }, 600);
  };

  const handleCreatePolicy = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPolicyName.trim()) {
      toast('Please enter a policy name', 'error');
      return;
    }

    const newPolicy: PatchPolicy = {
      id: `pol-${Date.now()}`,
      name: newPolicyName.trim(),
      ring: newPolicyRing,
      approvalMode: newPolicyApproval,
      maintenanceWindow: {
        day: newPolicyDay,
        startHour: newPolicyStartHour,
        durationHours: newPolicyDuration,
      },
      rebootPolicy: newPolicyReboot,
      retryCount: newPolicyRetryCount,
      retryDelayHours: newPolicyRetryDelay,
      exclusions: newPolicyExclusions
        ? newPolicyExclusions.split(',').map((s) => s.trim()).filter(Boolean)
        : [],
      deviceCount: 0,
      enabled: true,
    };

    setPolicies((prev) => [newPolicy, ...prev]);
    setIsPolicyModalOpen(false);
    toast(`Patch policy "${newPolicy.name}" created successfully`, 'success');

    // Reset form
    setNewPolicyName('');
    setNewPolicyRing('ring2');
    setNewPolicyApproval('automatic');
    setNewPolicyExclusions('');
  };

  const tabsList = [
    { id: 'overview', label: 'Overview' },
    { id: 'missing', label: 'Missing Updates', count: aggregatedMissing.length },
    { id: 'installed', label: 'Installed Patches', count: installedPatches.length },
    { id: 'failed', label: 'Failed Patches', count: failedPatches.length },
    { id: 'policies', label: 'Policies', count: policies.length },
    { id: 'history', label: 'History', count: jobs.length },
  ];

  return (
    <PageContainer>
      <PageHeader
        title="Patch Management"
        description="Monitor system update compliance, automate OS patch rings, and resolve failed updates across all endpoints."
        actions={
          <div className="flex items-center gap-3">
            <Badge
              variant={metrics.compliancePercent >= 90 ? 'online' : metrics.compliancePercent >= 75 ? 'warning' : 'critical'}
              className="px-3 py-1 text-xs font-semibold"
            >
              Overall Compliance: {metrics.compliancePercent}%
            </Badge>
            <Button size="sm" variant="secondary" onClick={fetchData} loading={loading}>
              <RefreshCw className="h-3.5 w-3.5" />
              Refresh
            </Button>
          </div>
        }
      />

      <div className="px-6">
        <Tabs tabs={tabsList} defaultActive="overview" onChange={setActiveTab} className="mb-6" />

        {loading && activeTab === 'overview' ? (
          <LoadingState rows={6} />
        ) : (
          <>
            {/* OVERVIEW TAB */}
            {activeTab === 'overview' && (
              <div className="space-y-6 animate-fade-in">
                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                  <StatCard
                    label="Compliance Rate"
                    value={`${metrics.compliancePercent}%`}
                    sublabel={`${metrics.compliant} of ${devices.length} devices compliant`}
                    status={metrics.compliancePercent >= 90 ? 'online' : metrics.compliancePercent >= 75 ? 'warning' : 'critical'}
                  />
                  <StatCard
                    label="Compliant Devices"
                    value={metrics.compliant}
                    sublabel="Up to date"
                    status="online"
                  />
                  <StatCard
                    label="Missing Updates"
                    value={metrics.missing}
                    sublabel={`${aggregatedMissing.length} unique patches missing`}
                    status="warning"
                  />
                  <StatCard
                    label="Failed Patch Installs"
                    value={metrics.failed}
                    sublabel="Requires intervention"
                    status="critical"
                  />
                  <StatCard
                    label="Active Policies"
                    value={policies.filter((p) => p.enabled).length}
                    sublabel={`Across ${policies.length} deployment rings`}
                    status="info"
                  />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Gauge & Score Card */}
                  <Card className="lg:col-span-1">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <ShieldCheck className="h-4 w-4 text-accent" />
                        Compliance Gauge
                      </CardTitle>
                    </CardHeader>
                    <CardBody className="flex flex-col items-center justify-center py-6">
                      <div className="relative flex items-center justify-center w-40 h-40">
                        <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                          <circle
                            cx="50"
                            cy="50"
                            r="42"
                            stroke="var(--border)"
                            strokeWidth="10"
                            fill="transparent"
                          />
                          <circle
                            cx="50"
                            cy="50"
                            r="42"
                            stroke={
                              metrics.compliancePercent >= 90
                                ? 'var(--status-online)'
                                : metrics.compliancePercent >= 75
                                ? 'var(--status-warning)'
                                : 'var(--status-critical)'
                            }
                            strokeWidth="10"
                            strokeDasharray={264}
                            strokeDashoffset={264 - (264 * metrics.compliancePercent) / 100}
                            strokeLinecap="round"
                            fill="transparent"
                            className="transition-all duration-1000 ease-out"
                          />
                        </svg>
                        <div className="absolute flex flex-col items-center justify-center text-center">
                          <span className="text-3xl font-bold tabular text-text-primary">
                            {metrics.compliancePercent}%
                          </span>
                          <span className="text-2xs text-text-muted uppercase tracking-wider font-semibold">
                            Compliant
                          </span>
                        </div>
                      </div>

                      <div className="mt-6 w-full space-y-2 text-xs">
                        <div className="flex justify-between items-center py-1.5 border-b border-border">
                          <span className="text-text-secondary flex items-center gap-1.5">
                            <span className="h-2 w-2 rounded-full bg-status-online" />
                            Compliant Endpoints
                          </span>
                          <span className="font-semibold text-text-primary">{metrics.compliant}</span>
                        </div>
                        <div className="flex justify-between items-center py-1.5 border-b border-border">
                          <span className="text-text-secondary flex items-center gap-1.5">
                            <span className="h-2 w-2 rounded-full bg-status-warning" />
                            Missing Updates
                          </span>
                          <span className="font-semibold text-text-primary">{metrics.missing}</span>
                        </div>
                        <div className="flex justify-between items-center py-1.5">
                          <span className="text-text-secondary flex items-center gap-1.5">
                            <span className="h-2 w-2 rounded-full bg-status-critical" />
                            Failed Updates
                          </span>
                          <span className="font-semibold text-text-primary">{metrics.failed}</span>
                        </div>
                      </div>
                    </CardBody>
                  </Card>

                  {/* Missing Severity & Category Breakdown */}
                  <Card className="lg:col-span-2">
                    <CardHeader>
                      <CardTitle className="flex items-center justify-between">
                        <span className="flex items-center gap-2">
                          <ShieldAlert className="h-4 w-4 text-status-warning" />
                          Missing Patch Severity & Category Breakdown
                        </span>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setActiveTab('missing')}
                          className="text-xs"
                        >
                          View All ({aggregatedMissing.length})
                          <ChevronRight className="h-3.5 w-3.5 ml-1" />
                        </Button>
                      </CardTitle>
                    </CardHeader>
                    <CardBody className="space-y-6">
                      <div className="grid grid-cols-3 gap-4">
                        <div className="p-3 surface-elevated border border-status-critical/30 rounded-md">
                          <div className="text-xs text-status-critical font-medium uppercase tracking-wide">
                            Critical Severity
                          </div>
                          <div className="text-2xl font-bold text-status-critical mt-1">
                            {aggregatedMissing.filter((p) => p.severity === 'critical').length}
                          </div>
                          <div className="text-2xs text-text-muted mt-1">
                            Impacts {metrics.criticalMissing} endpoint instances
                          </div>
                        </div>

                        <div className="p-3 surface-elevated border border-status-warning/30 rounded-md">
                          <div className="text-xs text-status-warning font-medium uppercase tracking-wide">
                            Important Severity
                          </div>
                          <div className="text-2xl font-bold text-status-warning mt-1">
                            {aggregatedMissing.filter((p) => p.severity === 'important').length}
                          </div>
                          <div className="text-2xs text-text-muted mt-1">
                            Impacts {metrics.importantMissing} endpoint instances
                          </div>
                        </div>

                        <div className="p-3 surface-elevated border border-border rounded-md">
                          <div className="text-xs text-text-muted font-medium uppercase tracking-wide">
                            Optional / Drivers
                          </div>
                          <div className="text-2xl font-bold text-text-primary mt-1">
                            {aggregatedMissing.filter((p) => p.severity === 'optional').length}
                          </div>
                          <div className="text-2xs text-text-muted mt-1">Non-critical enhancements</div>
                        </div>
                      </div>

                      <div>
                        <h4 className="text-xs font-semibold text-text-secondary uppercase tracking-wider mb-3">
                          Top Missing Patches Across Fleet
                        </h4>
                        <div className="space-y-2">
                          {aggregatedMissing.slice(0, 4).map((patch) => (
                            <div
                              key={patch.kb}
                              className="flex items-center justify-between p-2.5 surface-elevated rounded hover:border-border-strong transition-colors"
                            >
                              <div className="flex items-center gap-3 min-w-0">
                                <Badge
                                  variant={
                                    patch.severity === 'critical'
                                      ? 'critical'
                                      : patch.severity === 'important'
                                      ? 'warning'
                                      : 'default'
                                  }
                                >
                                  {patch.kb}
                                </Badge>
                                <div className="min-w-0">
                                  <div className="text-xs font-medium text-text-primary truncate">
                                    {patch.title}
                                  </div>
                                  <div className="text-2xs text-text-muted flex items-center gap-2">
                                    <span>{patch.category}</span>
                                    <span>•</span>
                                    <span>{patch.sizeMb} MB</span>
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-3 shrink-0">
                                <span className="text-xs font-medium text-status-warning">
                                  {patch.affectedDevices.length} devices
                                </span>
                                <Button
                                  size="sm"
                                  variant="secondary"
                                  onClick={() => handleInstallSinglePatch(patch.kb, patch.title)}
                                  loading={installingKb === patch.kb}
                                >
                                  Install
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </div>

                {/* Recent Patch Jobs */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        <Activity className="h-4 w-4 text-accent" />
                        Recent Patch Jobs
                      </span>
                      <Button size="sm" variant="ghost" onClick={() => setActiveTab('history')}>
                        View History
                      </Button>
                    </CardTitle>
                  </CardHeader>
                  <CardBody className="p-0">
                    <div className="overflow-x-auto">
                      <table className="w-full text-left text-xs">
                        <thead className="bg-base-elevated text-text-muted border-b border-border font-medium">
                          <tr>
                            <th className="py-2.5 px-4">Job Name</th>
                            <th className="py-2.5 px-4">Status</th>
                            <th className="py-2.5 px-4">Target Devices</th>
                            <th className="py-2.5 px-4">Progress</th>
                            <th className="py-2.5 px-4">Initiated By</th>
                            <th className="py-2.5 px-4">Created</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border text-text-secondary">
                          {jobs.slice(0, 5).map((job) => (
                            <tr key={job.id} className="hover:bg-base-hover transition-colors">
                              <td className="py-3 px-4 font-medium text-text-primary">{job.name}</td>
                              <td className="py-3 px-4">
                                <Badge
                                  variant={
                                    job.status === 'completed'
                                      ? 'online'
                                      : job.status === 'running'
                                      ? 'warning'
                                      : job.status === 'failed'
                                      ? 'critical'
                                      : 'default'
                                  }
                                >
                                  {job.status}
                                </Badge>
                              </td>
                              <td className="py-3 px-4 tabular">
                                {job.completedCount + job.failedCount} / {job.targetCount}
                              </td>
                              <td className="py-3 px-4 min-w-[140px]">
                                <div className="flex items-center gap-2">
                                  <div className="flex-1 bg-base-hover h-1.5 rounded-full overflow-hidden">
                                    <div
                                      className="bg-accent h-full rounded-full transition-all duration-300"
                                      style={{ width: `${job.progress}%` }}
                                    />
                                  </div>
                                  <span className="text-2xs tabular text-text-muted">{job.progress}%</span>
                                </div>
                              </td>
                              <td className="py-3 px-4">{job.initiatedBy}</td>
                              <td className="py-3 px-4 text-text-muted">{timeAgo(job.createdAt)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardBody>
                </Card>
              </div>
            )}

            {/* MISSING UPDATES TAB */}
            {activeTab === 'missing' && (
              <div className="space-y-4 animate-fade-in">
                <Card>
                  <CardBody className="p-4 flex flex-col md:flex-row gap-3 items-center justify-between">
                    <div className="flex flex-col md:flex-row gap-3 items-center w-full md:w-auto">
                      <div className="relative w-full md:w-72">
                        <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-text-muted" />
                        <Input
                          placeholder="Search KB or title..."
                          value={missingSearch}
                          onChange={(e) => setMissingSearch(e.target.value)}
                          className="pl-8"
                        />
                      </div>
                      <Select
                        value={missingSeverity}
                        onChange={(e) => setMissingSeverity(e.target.value)}
                        className="w-full md:w-40"
                      >
                        <option value="all">All Severities</option>
                        <option value="critical">Critical</option>
                        <option value="important">Important</option>
                        <option value="optional">Optional</option>
                      </Select>
                      <Select
                        value={missingCategory}
                        onChange={(e) => setMissingCategory(e.target.value)}
                        className="w-full md:w-48"
                      >
                        <option value="all">All Categories</option>
                        {missingCategories.map((c) => (
                          <option key={c} value={c}>
                            {c}
                          </option>
                        ))}
                      </Select>
                    </div>

                    <div className="flex items-center gap-2 w-full md:w-auto justify-end">
                      {selectedMissingKbs.length > 0 && (
                        <Button size="sm" variant="primary" onClick={handleInstallSelected}>
                          <Play className="h-3.5 w-3.5" />
                          Install Selected ({selectedMissingKbs.length})
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => {
                          const criticals = aggregatedMissing.filter((p) => p.severity === 'critical').map((p) => p.kb);
                          setSelectedMissingKbs(criticals);
                        }}
                      >
                        Select All Critical
                      </Button>
                    </div>
                  </CardBody>
                </Card>

                {filteredMissing.length === 0 ? (
                  <EmptyState
                    icon={<ShieldCheck className="h-10 w-10 text-status-online" />}
                    title="No missing updates found"
                    description="All endpoints meet current patch criteria or match your active filter settings."
                  />
                ) : (
                  <Card>
                    <CardBody className="p-0">
                      <div className="overflow-x-auto">
                        <table className="w-full text-left text-xs">
                          <thead className="bg-base-elevated text-text-muted border-b border-border font-medium">
                            <tr>
                              <th className="py-3 px-4 w-10">
                                <Checkbox
                                  checked={
                                    selectedMissingKbs.length === filteredMissing.length &&
                                    filteredMissing.length > 0
                                  }
                                  onChange={(checked) =>
                                    setSelectedMissingKbs(checked ? filteredMissing.map((p) => p.kb) : [])
                                  }
                                />
                              </th>
                              <th className="py-3 px-4">KB Number</th>
                              <th className="py-3 px-4">Patch Title</th>
                              <th className="py-3 px-4">Severity</th>
                              <th className="py-3 px-4">Category</th>
                              <th className="py-3 px-4">Affected Devices</th>
                              <th className="py-3 px-4">Size</th>
                              <th className="py-3 px-4 text-right">Action</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-border text-text-secondary">
                            {filteredMissing.map((patch) => {
                              const isSelected = selectedMissingKbs.includes(patch.kb);
                              return (
                                <tr
                                  key={patch.kb}
                                  className={cn(
                                    'hover:bg-base-hover transition-colors',
                                    isSelected && 'bg-accent-muted/30'
                                  )}
                                >
                                  <td className="py-3 px-4">
                                    <Checkbox
                                      checked={isSelected}
                                      onChange={(checked) =>
                                        setSelectedMissingKbs((prev) =>
                                          checked ? [...prev, patch.kb] : prev.filter((k) => k !== patch.kb)
                                        )
                                      }
                                    />
                                  </td>
                                  <td className="py-3 px-4 font-mono font-medium text-text-primary">
                                    {patch.kb}
                                  </td>
                                  <td className="py-3 px-4 max-w-md font-medium text-text-primary">
                                    {patch.title}
                                  </td>
                                  <td className="py-3 px-4">
                                    <Badge
                                      variant={
                                        patch.severity === 'critical'
                                          ? 'critical'
                                          : patch.severity === 'important'
                                          ? 'warning'
                                          : 'default'
                                      }
                                    >
                                      {patch.severity}
                                    </Badge>
                                  </td>
                                  <td className="py-3 px-4 text-text-muted">{patch.category}</td>
                                  <td className="py-3 px-4">
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-2xs font-semibold bg-base-elevated border border-border text-status-warning">
                                      {patch.affectedDevices.length} devices
                                    </span>
                                  </td>
                                  <td className="py-3 px-4 tabular text-text-muted">{patch.sizeMb} MB</td>
                                  <td className="py-3 px-4 text-right">
                                    <Button
                                      size="sm"
                                      variant="secondary"
                                      onClick={() => handleInstallSinglePatch(patch.kb, patch.title)}
                                      loading={installingKb === patch.kb}
                                    >
                                      Install
                                    </Button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </CardBody>
                  </Card>
                )}
              </div>
            )}

            {/* INSTALLED PATCHES TAB */}
            {activeTab === 'installed' && (
              <div className="space-y-4 animate-fade-in">
                <Card>
                  <CardBody className="p-4 flex flex-col md:flex-row gap-3 items-center justify-between">
                    <div className="flex flex-col md:flex-row gap-3 items-center w-full md:w-auto">
                      <div className="relative w-full md:w-72">
                        <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-text-muted" />
                        <Input
                          placeholder="Search installed patches or device..."
                          value={installedSearch}
                          onChange={(e) => setInstalledSearch(e.target.value)}
                          className="pl-8"
                        />
                      </div>
                      <Select
                        value={installedCategory}
                        onChange={(e) => setInstalledCategory(e.target.value)}
                        className="w-full md:w-48"
                      >
                        <option value="all">All Categories</option>
                        <option value="Security Updates">Security Updates</option>
                        <option value="Cumulative Updates">Cumulative Updates</option>
                        <option value="Critical Updates">Critical Updates</option>
                        <option value="Drivers">Drivers</option>
                      </Select>
                    </div>
                    <span className="text-xs text-text-muted">
                      Showing {filteredInstalled.length} installed patch records
                    </span>
                  </CardBody>
                </Card>

                {filteredInstalled.length === 0 ? (
                  <EmptyState
                    icon={<FileText className="h-10 w-10 text-text-disabled" />}
                    title="No installed patch records"
                    description="No installed patches matched your search parameters."
                  />
                ) : (
                  <Card>
                    <CardBody className="p-0">
                      <div className="overflow-x-auto">
                        <table className="w-full text-left text-xs">
                          <thead className="bg-base-elevated text-text-muted border-b border-border font-medium">
                            <tr>
                              <th className="py-3 px-4">KB Number</th>
                              <th className="py-3 px-4">Title</th>
                              <th className="py-3 px-4">Device Hostname</th>
                              <th className="py-3 px-4">Client / Site</th>
                              <th className="py-3 px-4">Category</th>
                              <th className="py-3 px-4">Installed Date</th>
                              <th className="py-3 px-4">Status</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-border text-text-secondary">
                            {filteredInstalled.map((patch) => (
                              <tr key={patch.id} className="hover:bg-base-hover transition-colors">
                                <td className="py-3 px-4 font-mono font-medium text-text-primary">
                                  {patch.kb}
                                </td>
                                <td className="py-3 px-4 max-w-sm font-medium text-text-primary">
                                  {patch.title}
                                </td>
                                <td className="py-3 px-4">
                                  <button
                                    onClick={() => navigate(`/devices/${patch.deviceId}`)}
                                    className="font-medium text-accent hover:underline flex items-center gap-1"
                                  >
                                    <Server className="h-3 w-3" />
                                    {patch.hostname}
                                  </button>
                                </td>
                                <td className="py-3 px-4 text-text-muted">
                                  {patch.client} ({patch.site})
                                </td>
                                <td className="py-3 px-4 text-text-muted">{patch.category}</td>
                                <td className="py-3 px-4 text-text-muted">{formatDate(patch.installedDate)}</td>
                                <td className="py-3 px-4">
                                  <Badge variant="online">Installed</Badge>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardBody>
                  </Card>
                )}
              </div>
            )}

            {/* FAILED PATCHES TAB */}
            {activeTab === 'failed' && (
              <div className="space-y-4 animate-fade-in">
                <Card>
                  <CardBody className="p-4 flex flex-col md:flex-row gap-3 items-center justify-between">
                    <div className="relative w-full md:w-80">
                      <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-text-muted" />
                      <Input
                        placeholder="Search failed patches, device, error..."
                        value={failedSearch}
                        onChange={(e) => setFailedSearch(e.target.value)}
                        className="pl-8"
                      />
                    </div>
                    <span className="text-xs text-status-critical font-medium">
                      {filteredFailed.length} Failed Installation Attempt(s)
                    </span>
                  </CardBody>
                </Card>

                {filteredFailed.length === 0 ? (
                  <EmptyState
                    icon={<CheckCircle2 className="h-10 w-10 text-status-online" />}
                    title="No failed patch installations"
                    description="All update installations have completed cleanly without errors."
                  />
                ) : (
                  <Card>
                    <CardBody className="p-0">
                      <div className="overflow-x-auto">
                        <table className="w-full text-left text-xs">
                          <thead className="bg-base-elevated text-text-muted border-b border-border font-medium">
                            <tr>
                              <th className="py-3 px-4">KB Number</th>
                              <th className="py-3 px-4">Patch Title</th>
                              <th className="py-3 px-4">Target Device</th>
                              <th className="py-3 px-4">Error Detail</th>
                              <th className="py-3 px-4">Failed Date</th>
                              <th className="py-3 px-4 text-right">Action</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-border text-text-secondary">
                            {filteredFailed.map((record) => (
                              <tr key={record.id} className="hover:bg-base-hover transition-colors">
                                <td className="py-3 px-4 font-mono font-medium text-status-critical">
                                  {record.kb}
                                </td>
                                <td className="py-3 px-4 font-medium text-text-primary max-w-xs">
                                  {record.title}
                                </td>
                                <td className="py-3 px-4">
                                  <button
                                    onClick={() => navigate(`/devices/${record.deviceId}`)}
                                    className="font-medium text-accent hover:underline flex items-center gap-1"
                                  >
                                    <Server className="h-3 w-3" />
                                    {record.hostname}
                                  </button>
                                </td>
                                <td className="py-3 px-4">
                                  <span className="font-mono text-2xs text-status-critical bg-status-critical/10 px-2 py-1 rounded border border-status-critical/20">
                                    {record.error}
                                  </span>
                                </td>
                                <td className="py-3 px-4 text-text-muted">{timeAgo(record.date)}</td>
                                <td className="py-3 px-4 text-right">
                                  <Button
                                    size="sm"
                                    variant="secondary"
                                    onClick={() => handleRetryPatch(record)}
                                    loading={retryingPatchId === record.id}
                                  >
                                    <RotateCcw className="h-3 w-3 mr-1" />
                                    Retry
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardBody>
                  </Card>
                )}
              </div>
            )}

            {/* POLICIES TAB */}
            {activeTab === 'policies' && (
              <div className="space-y-4 animate-fade-in">
                <div className="flex items-center justify-between">
                  <p className="text-xs text-text-muted">
                    Patch policies control staged rollout rings, maintenance windows, and automatic reboot rules.
                  </p>
                  <Button size="sm" variant="primary" onClick={() => setIsPolicyModalOpen(true)}>
                    <Plus className="h-3.5 w-3.5" />
                    New Policy
                  </Button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {policies.map((policy) => (
                    <Card key={policy.id} className={cn(!policy.enabled && 'opacity-60')}>
                      <CardHeader className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <CardTitle>{policy.name}</CardTitle>
                          <Badge
                            variant={
                              policy.ring === 'ring1'
                                ? 'critical'
                                : policy.ring === 'ring2'
                                ? 'warning'
                                : policy.ring === 'ring3'
                                ? 'online'
                                : 'info'
                            }
                          >
                            {policy.ring.toUpperCase()}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleTogglePolicy(policy.id)}
                            className={cn(
                              'relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none',
                              policy.enabled ? 'bg-accent' : 'bg-base-elevated border-border'
                            )}
                          >
                            <span
                              className={cn(
                                'pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                                policy.enabled ? 'translate-x-4' : 'translate-x-0'
                              )}
                            />
                          </button>
                        </div>
                      </CardHeader>
                      <CardBody className="space-y-3 text-xs">
                        <div className="grid grid-cols-2 gap-2 text-text-muted">
                          <div>
                            <span className="block text-2xs uppercase tracking-wider text-text-disabled">
                              Approval Mode
                            </span>
                            <span className="font-semibold text-text-primary capitalize">
                              {policy.approvalMode}
                            </span>
                          </div>
                          <div>
                            <span className="block text-2xs uppercase tracking-wider text-text-disabled">
                              Reboot Policy
                            </span>
                            <span className="font-semibold text-text-primary capitalize">
                              {policy.rebootPolicy.replace('_', ' ')}
                            </span>
                          </div>
                          <div>
                            <span className="block text-2xs uppercase tracking-wider text-text-disabled">
                              Maintenance Window
                            </span>
                            <span className="font-semibold text-text-primary">
                              {policy.maintenanceWindow.day} @ {policy.maintenanceWindow.startHour}:00 (
                              {policy.maintenanceWindow.durationHours}h)
                            </span>
                          </div>
                          <div>
                            <span className="block text-2xs uppercase tracking-wider text-text-disabled">
                              Target Endpoints
                            </span>
                            <span className="font-semibold text-text-primary">
                              {policy.deviceCount} devices
                            </span>
                          </div>
                        </div>

                        {policy.exclusions.length > 0 && (
                          <div className="pt-2 border-t border-border">
                            <span className="text-2xs text-text-disabled uppercase tracking-wider block mb-1">
                              Exclusions ({policy.exclusions.length}):
                            </span>
                            <div className="flex flex-wrap gap-1">
                              {policy.exclusions.map((ex) => (
                                <Badge key={ex} variant="default" className="text-2xs">
                                  {ex}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </CardBody>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* HISTORY TAB */}
            {activeTab === 'history' && (
              <div className="space-y-4 animate-fade-in">
                <Card>
                  <CardBody className="p-4 flex flex-col md:flex-row gap-3 items-center justify-between">
                    <div className="flex flex-col md:flex-row gap-3 items-center w-full md:w-auto">
                      <div className="relative w-full md:w-72">
                        <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-text-muted" />
                        <Input
                          placeholder="Search patch jobs..."
                          value={historySearch}
                          onChange={(e) => setHistorySearch(e.target.value)}
                          className="pl-8"
                        />
                      </div>
                      <Select
                        value={historyStatus}
                        onChange={(e) => setHistoryStatus(e.target.value)}
                        className="w-full md:w-40"
                      >
                        <option value="all">All Statuses</option>
                        <option value="completed">Completed</option>
                        <option value="running">Running</option>
                        <option value="failed">Failed</option>
                        <option value="pending">Pending</option>
                      </Select>
                    </div>
                  </CardBody>
                </Card>

                {filteredJobs.length === 0 ? (
                  <EmptyState
                    icon={<Clock className="h-10 w-10 text-text-disabled" />}
                    title="No patch job history"
                    description="No execution history records match your search query."
                  />
                ) : (
                  <Card>
                    <CardBody className="p-0">
                      <div className="overflow-x-auto">
                        <table className="w-full text-left text-xs">
                          <thead className="bg-base-elevated text-text-muted border-b border-border font-medium">
                            <tr>
                              <th className="py-3 px-4">Job ID</th>
                              <th className="py-3 px-4">Job Name</th>
                              <th className="py-3 px-4">Status</th>
                              <th className="py-3 px-4">Target Count</th>
                              <th className="py-3 px-4">Completed</th>
                              <th className="py-3 px-4">Failed</th>
                              <th className="py-3 px-4">Initiated By</th>
                              <th className="py-3 px-4">Date</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-border text-text-secondary">
                            {filteredJobs.map((job) => (
                              <tr key={job.id} className="hover:bg-base-hover transition-colors">
                                <td className="py-3 px-4 font-mono text-2xs text-text-muted">{job.id}</td>
                                <td className="py-3 px-4 font-medium text-text-primary">{job.name}</td>
                                <td className="py-3 px-4">
                                  <Badge
                                    variant={
                                      job.status === 'completed'
                                        ? 'online'
                                        : job.status === 'running'
                                        ? 'warning'
                                        : job.status === 'failed'
                                        ? 'critical'
                                        : 'default'
                                    }
                                  >
                                    {job.status}
                                  </Badge>
                                </td>
                                <td className="py-3 px-4 tabular">{job.targetCount}</td>
                                <td className="py-3 px-4 tabular text-status-online">{job.completedCount}</td>
                                <td className="py-3 px-4 tabular text-status-critical">{job.failedCount}</td>
                                <td className="py-3 px-4 text-text-muted">{job.initiatedBy}</td>
                                <td className="py-3 px-4 text-text-muted">{formatDate(job.createdAt)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardBody>
                  </Card>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* NEW POLICY MODAL */}
      <Modal
        open={isPolicyModalOpen}
        onClose={() => setIsPolicyModalOpen(false)}
        title="Create Patch Policy"
        size="lg"
      >
        <form onSubmit={handleCreatePolicy} className="space-y-4 text-xs">
          <div>
            <Label>Policy Name</Label>
            <Input
              placeholder="e.g., Workstation Ring 2 Automatic"
              value={newPolicyName}
              onChange={(e) => setNewPolicyName(e.target.value)}
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Rollout Ring</Label>
              <Select
                value={newPolicyRing}
                onChange={(e) => setNewPolicyRing(e.target.value as any)}
              >
                <option value="ring1">Ring 1 - Canary / Test</option>
                <option value="ring2">Ring 2 - Early Adopters</option>
                <option value="ring3">Ring 3 - Broad Deployment</option>
                <option value="ring4">Ring 4 - Critical Servers</option>
              </Select>
            </div>

            <div>
              <Label>Approval Mode</Label>
              <Select
                value={newPolicyApproval}
                onChange={(e) => setNewPolicyApproval(e.target.value as any)}
              >
                <option value="automatic">Automatic Approval</option>
                <option value="manual">Manual Approval Required</option>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Maintenance Day</Label>
              <Select value={newPolicyDay} onChange={(e) => setNewPolicyDay(e.target.value)}>
                {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </Select>
            </div>

            <div>
              <Label>Start Hour (0-23)</Label>
              <Input
                type="number"
                min={0}
                max={23}
                value={newPolicyStartHour}
                onChange={(e) => setNewPolicyStartHour(parseInt(e.target.value) || 0)}
              />
            </div>

            <div>
              <Label>Duration (Hours)</Label>
              <Input
                type="number"
                min={1}
                max={12}
                value={newPolicyDuration}
                onChange={(e) => setNewPolicyDuration(parseInt(e.target.value) || 1)}
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Reboot Policy</Label>
              <Select
                value={newPolicyReboot}
                onChange={(e) => setNewPolicyReboot(e.target.value as any)}
              >
                <option value="when_required">When Required</option>
                <option value="always">Always Reboot</option>
                <option value="never">Never Reboot</option>
              </Select>
            </div>

            <div>
              <Label>Retry Count</Label>
              <Input
                type="number"
                min={1}
                max={5}
                value={newPolicyRetryCount}
                onChange={(e) => setNewPolicyRetryCount(parseInt(e.target.value) || 1)}
              />
            </div>

            <div>
              <Label>Retry Delay (Hours)</Label>
              <Input
                type="number"
                min={1}
                max={24}
                value={newPolicyRetryDelay}
                onChange={(e) => setNewPolicyRetryDelay(parseInt(e.target.value) || 1)}
              />
            </div>
          </div>

          <div>
            <Label>KB Exclusions (comma separated)</Label>
            <Input
              placeholder="e.g., KB5034123, KB5034124"
              value={newPolicyExclusions}
              onChange={(e) => setNewPolicyExclusions(e.target.value)}
            />
          </div>

          <div className="flex justify-end gap-2 pt-3 border-t border-border">
            <Button type="button" variant="ghost" onClick={() => setIsPolicyModalOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="primary">
              Create Policy
            </Button>
          </div>
        </form>
      </Modal>
    </PageContainer>
  );
}
